import { useCallback } from "react";
import { QueryKey, useQueryClient } from "@tanstack/react-query";
import { ProjectManagersService } from "@/modules/projects/service/projectManagers";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useTranslation } from "react-i18next";
import { useCheckIsProjectLocked } from "./useCheckIsProjectLocked";
import { RowSelectionStore } from "@/components/data-table/context/RowSelectionContext";

export const useProjectManagersAddUsersForm = ({
  store,
  projectId,
  onSuccess,
  managersDirectoryQueryKey,
}: {
  store: RowSelectionStore;
  projectId: string;
  onSuccess: () => void;
  managersDirectoryQueryKey: QueryKey;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const isProjectLocked = useCheckIsProjectLocked();

  const mutation = useMutationWithErrorHandling({
    mutationFn: (checkedManagerIds: string[]) =>
      ProjectManagersService.addUsers(projectId, checkedManagerIds),
    errorTitle: t("projects.managers.addUsers.errors.addUsers"),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: managersDirectoryQueryKey,
      });
      store.clear();
      onSuccess();
    },
  });

  const handleSubmit = useCallback(async () => {
    if (isProjectLocked) return;

    const checkedManagerIds = Array.from(store.getSelection());

    if (checkedManagerIds.length === 0) return;

    await mutation.mutateAsync(checkedManagerIds);
  }, [mutation, isProjectLocked, store]);

  return {
    handleSubmit,
    isSubmitting: mutation.isPending,
  };
};
